"use client";

import React from "react";
import { User, Calendar, CircleAlert, Loader, ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 animate-pulse">
        <div className="h-8 bg-muted rounded-lg w-3/4 mx-auto mb-6"></div>
        <div className="h-4 bg-muted rounded-lg w-1/2 mx-auto mb-12"></div>
        <div className="w-full aspect-video bg-muted rounded-lg mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-lg w-full"></div>
          <div className="h-4 bg-muted rounded-lg w-full"></div>
          <div className="h-4 bg-muted rounded-lg w-5/6"></div>
          <div className="h-4 bg-muted rounded-lg w-4/5"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <CircleAlert className="w-12 h-12 text-destructive mx-auto mb-4" />
        <h2 className="text-2xl font-bold font-default text-foreground mb-2">Error loading story</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={refetch} variant="outline" className="rounded-lg">
          Try again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl font-bold font-default text-foreground mb-4">Story not found</h2>
        <p className="text-muted-foreground mb-8">The impact story you are looking for is currently unavailable.</p>
        <Link to="/FieldReportsPage">
          <Button className="rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Field Reports
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto px-4 py-12 md:py-20">
      {}
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {post.categories.map((cat, i) => (
              <Link key={cat.id} to={cat.link || "#"}>
                <span data-index={i} className="text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 hover:bg-primary/20 transition-colors px-4 py-1.5 rounded-lg">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground mb-6 leading-tight tracking-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-4 text-muted-foreground text-sm font-default">
          {post.author && (
            <div className="flex items-center gap-2">
              {post.author.avatar && post.author.avatar['24'] ? (
                <img src={post.author.avatar['24']} alt={post.author.name} className="w-6 h-6 rounded-lg" />
              ) : (
                <User className="w-4 h-4" />
              )}
              <span className="font-semibold text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-lg overflow-hidden shadow-lg bg-muted">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover aspect-video"
            loading="eager"
          />
        </div>
      )}

      {}
      <div
        className="text-foreground text-lg leading-relaxed font-default [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:mb-4 [&>h2]:mt-12 [&>h2]:text-foreground [&>h2]:tracking-tight [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:text-foreground [&>h4]:text-xl [&>h4]:font-bold [&>h4]:font-default [&>h4]:mb-3 [&>h4]:mt-6 [&>h4]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:text-muted-foreground [&>blockquote]:text-xl [&>blockquote]:bg-muted/30 [&>blockquote]:py-4 [&>blockquote]:rounded-r-lg [&>img]:rounded-lg [&>img]:my-8 [&>img]:shadow-md [&>img]:w-full [&>img]:h-auto [&>figure]:my-8 [&>figure>img]:rounded-lg [&>figure>img]:shadow-md [&>figure>img]:w-full [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-muted-foreground [&>figure>figcaption]:mt-3 [&>a]:text-primary [&>a]:font-semibold [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>pre]:bg-muted [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:mb-6 [&>pre]:text-sm [&>code]:bg-muted [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-lg [&>code]:text-sm"
        dangerouslySetInnerHTML={{ __html: post.content || '' }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-muted-foreground mr-2 uppercase tracking-wider">Impact Areas:</span>
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"}>
              <span data-index={i} className="text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors px-4 py-1 rounded-lg">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {}
      {post.author && (
        <Card className="mt-12 bg-card border-border shadow-md overflow-hidden rounded-lg">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {post.author.avatar && post.author.avatar['96'] ? (
              <img 
                src={post.author.avatar['96']} 
                alt={post.author.name} 
                className="w-24 h-24 rounded-lg object-cover border-2 border-border shadow-sm shrink-0" 
              />
            ) : (
              <div className="w-24 h-24 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <User className="w-10 h-10" />
              </div>
            )}
            <div className="text-center sm:text-left flex-1">
              <h3 className="text-xl font-bold font-default text-foreground mb-2">
                Reported by {post.author.name}
              </h3>
              <p className="text-muted-foreground mb-4 leading-relaxed font-default">
                Dedicated member of the Helping Hands Foundation team, documenting our global impact and sharing the stories of resilience from the communities we serve.
              </p>
              {post.author.link && (
                <Link to={post.author.link}>
                  <Button variant="outline" size="sm" className="rounded-lg border-primary text-primary hover:bg-primary/5">
                    View all reports by this author
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="242817" id="single-post-template-section" className="bg-background text-foreground w-full min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}